import memoize from 'lodash/memoize.js';
import dayjs from 'dayjs';
import caLabels from './data/CourseAssemblyLabels.js';
import tools from './data/tools.js';
import { NGA_SUBTYPES } from './constants.js';

/**
 * Returns the available tool ids
 * @returns {Object}
 */

const getToolIds = memoize(() =>
  tools.reduce((list, tool) => {
    list[tool.id] = tool.value;
    return list;
  }, {})
);

/**
 * Returns the subtype ids
 * @returns {Object}
 */
const getLabelIds = memoize(() =>
  caLabels.reduce((list, label) => {
    list[label.id] = label.labelKey;
    return list;
  }, {})
);

const TOOLS_HIDE_SIDE_PANEL = [getToolIds().manual, getToolIds().reef, getToolIds().iclicker];

const UNKNOWN_TOOL_LABEL = 'Unknown';

/**
 * Returns the corresponding tool
 * @param {String} tool
 * @returns {Object}
 */
function getTool (tool) {
  return tools.find((x) => x.value === tool) || null;
}

/**
 * Returns the tool label of the selected tool
 * @param {string} tool
 * @param {string} subtype
 * @param {string} labelKey
 * @returns {string}
 */
function getToolLabel (tool, subtype, labelKey = 'toolLabel') {
  const selectedTool = getTool(tool);
  if (!subtype) {
    return selectedTool && selectedTool.labels
      ? selectedTool.labels[labelKey]
      : UNKNOWN_TOOL_LABEL;
  }

  const toolSubtype = selectedTool
    ? selectedTool.subtypes.find((x) => x.id === subtype)
    : null;

  if (!toolSubtype) return UNKNOWN_TOOL_LABEL;
  return toolSubtype.labels ? toolSubtype.labels[labelKey] : UNKNOWN_TOOL_LABEL;
}

/**
 * Determines whether a tool only gets grades from instructors entering the grade manually (from GB or the tool itself)
 * @param {String} tool
 * @returns {Boolean}
 */
const toolIsManuallyGraded = (tool = '', subtype = '') => {
  const { assessment, goreact, manual, writing } = getToolIds();
  if (tool === assessment && subtype === NGA_SUBTYPES.SHORT_FORM_WRITING) {
    return true;
  }
  return [goreact, manual, writing].includes(tool);
};

const toolRemainsInProgressUntilDueDate = (tool) => {
  return ['smartworksheet'].includes(tool);
};

const toolSupportsOverdueLabel = (tool) => {
  const { learningcurve, mapi } = getToolIds();
  return [learningcurve, mapi].includes(tool);
};

const isInProgressUntilDueDate = (isPastDue, hasAssignmentGrade) => {
  return !!(isPastDue && hasAssignmentGrade);
}

/**
 * Returns true if the passed in tool type should hide the side panel
 * @param {string} tool
 * @returns {boolean}
 */
const shouldHideSidePanel = (tool) =>
  TOOLS_HIDE_SIDE_PANEL.includes(tool);

/**
 * Returns true if the passed in tool type is associated with Learning Curve
 * @param {String} testValue
 * @returns {Boolean}
 */
const isLearningCurve = (testValue) => {
  const toolIds = getToolIds();
  const learningCurveValues = {
    [toolIds.lcrp]: true,
    [toolIds.lc]: true,
    [toolIds.learningcurve]: true
  };
  return learningCurveValues[testValue];
};

const NEW_TAB_TOOLS = [
  getToolIds().url,
  getToolIds().instructorfile,
  getToolIds().staticfile
];

/**
 * Returns true if the passed in tool type should be launched in a new tab
 * @param {String} tool
 * @returns {Boolean}
 */
const isTabToolLaunch = (tool) => NEW_TAB_TOOLS.includes(tool);

const toolDefaultNames = {
  url: 'Unnamed URL',
  writing: 'Untitled Writing Activity',
  assessment: 'Unnamed Assessment',
  default: 'Unnamed Activity'
};

/**
 * Returns the default assignment name for the passed in tool
 * @param {String} tool
 * @returns {String}
 */
const getDefaultToolName = (tool) =>
  toolDefaultNames[tool] || toolDefaultNames.default;

/**
 * Returns true if the passed in assignment name is the default name for the passed in tool
 * @param {String} name
 * @param {String} tool
 * @returns {Boolean}
 */
const isUnnamed = (name, tool) => {
  if (name) {
    const defaultToolName = getDefaultToolName(tool);
    const defaultName = name
      .toLowerCase()
      .startsWith(defaultToolName.toLowerCase());
    return defaultName;
  }
  return true;
};

/**
 * Returns the default assignment name and date for the passed in tool
 * @param {String} name
 * @param {String} createdAt
 * @returns {String}
 */
const getUnnamedText = (tool, createdAt) =>
  `${getDefaultToolName(tool)} ${dayjs(createdAt).format('M/D/YYYY')}`;

/**
 * Returns the assignment name or default assignment name if there isn't one
 * @param {String} name
 * @param {String} tool
 * @param {String} createdAt
 * @returns {String}
 */
const formatName = (name, tool, createdAt) =>
  isUnnamed(name, tool) ? getUnnamedText(tool, createdAt) : name;

/**
 * Determines whether a tool is local launch or not
 * @param {String} tool
 * @returns {Boolean}
 */
const isLocalToolLaunch = (tool) => {
  const selectedTool = getTool(tool);
  return selectedTool ? selectedTool.isLocalToolLaunch : null;
}

const getLaunchUrl = (data) => {
  return `/${data.course_id}/test`;
}

const getStudentReportLink = (studentId, assignmentId) => {
  return `/${studentId}/test/${assignmentId}`;
}

export {
  getToolLabel,
  getLabelIds,
  getToolIds,
  UNKNOWN_TOOL_LABEL,
  toolIsManuallyGraded,
  toolRemainsInProgressUntilDueDate,
  isInProgressUntilDueDate,
  shouldHideSidePanel,
  isLearningCurve,
  isLocalToolLaunch,
  formatName,
  getUnnamedText,
  isUnnamed,
  getDefaultToolName,
  isTabToolLaunch,
  getTool,
  getLaunchUrl,
  getStudentReportLink,
  toolSupportsOverdueLabel
};
